import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import type { Rive } from '@rive-app/webgl2';
import type { RiveAnimationConfig } from 'shared/src/types/rive-animation-config';
import { loadRive } from 'shared/src/utils/rive.util';

export interface ModalData {
  title: string;
  message: string;
  okButtonTitle?: string;
  cancelButtonTitle?: string;
  rive?: RiveAnimationConfig;
  isConfirmation?: boolean;
}

@Component({
  selector: 'app-modal',
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('animation') riveAnimation: ElementRef;

  title: string;
  message: string;
  okButtonTitle: string;
  cancelButtonTitle: string;
  isConfirmation: boolean | undefined = false;
  rive: RiveAnimationConfig | undefined;

  private riveInstance: Rive | undefined;
  private isDestroyed = false;

  constructor(
    private dialogRef: MatDialogRef<ModalComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) data: ModalData
  ) {
    this.title = data.title;
    this.message = data.message;
    this.isConfirmation = data.isConfirmation;
    this.okButtonTitle = data.okButtonTitle || 'Ok';
    this.cancelButtonTitle = data.cancelButtonTitle || 'Cancel';
    this.rive = data.rive;
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.rive) {
      const canvas = this.riveAnimation.nativeElement;
      const { Rive } = await loadRive();

      // The dialog can be dismissed while the Rive chunk is still in flight; instantiating
      // then would leave a running animation nothing ever cleans up.
      if (this.isDestroyed) {
        return;
      }

      this.riveInstance = new Rive({
        src: `assets/rive/${this.rive.fileName}.riv`,
        canvas,
        autoplay: true,
        animations: this.rive.animations,
        onLoad: () => this.riveInstance?.resizeToCanvas(),
      });
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.riveInstance?.stop();
    this.riveInstance?.cleanup();
  }

  close(isOk: boolean) {
    this.dialogRef.close(isOk);
  }
}
